import { useCallback, useEffect, useRef, useState } from 'react'
import { errorMessage } from '@/api/client'

export type SaveState = 'idle' | 'saving' | 'saved' | 'error'

export interface SaveAction {
  status: SaveState
  error: string | null
  /** Runs `fn`, tracking its state. Resolves true on success; never throws. */
  run: (fn: () => Promise<unknown>) => Promise<boolean>
  reset: () => void
}

export const SAVED_VISIBLE_MS = 2500

export function useSaveAction(): SaveAction {
  const [status, setStatus] = useState<SaveState>('idle')
  const [error, setError] = useState<string | null>(null)
  const timer = useRef<ReturnType<typeof setTimeout>>()
  const mounted = useRef(true)

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
      clearTimeout(timer.current)
    }
  }, [])

  const run = useCallback(async (fn: () => Promise<unknown>) => {
    clearTimeout(timer.current)
    setStatus('saving')
    setError(null)
    try {
      await fn()
      if (!mounted.current) return true
      setStatus('saved')
      timer.current = setTimeout(() => mounted.current && setStatus('idle'), SAVED_VISIBLE_MS)
      return true
    } catch (err) {
      if (!mounted.current) return false
      setStatus('error')
      setError(errorMessage(err, "Couldn't save"))
      return false
    }
  }, [])

  const reset = useCallback(() => {
    clearTimeout(timer.current)
    setStatus('idle')
    setError(null)
  }, [])

  return { status, error, run, reset }
}

